import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, useWindowDimensions, Modal, ScrollView, TouchableWithoutFeedback, Platform } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { Field, Section, FIELD_LIMITS } from './studentForm.config';

const THEME_BLUE = '#1E40AF';

function CustomDropdown({ options, value, onChange, placeholder, disabled, readOnly }: any) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedLabel = options?.find((o: any) => o.value === value)?.label || placeholder || 'Select...';

  if (Platform.OS === 'web') {
    return React.createElement('select', {
      value: value || '',
      onChange: (e: any) => { if (!readOnly && !disabled) onChange(e.target.value); },
      disabled: readOnly || disabled,
      style: {
        width: '100%',
        padding: 12,
        borderRadius: 8,
        borderColor: '#ccc',
        borderWidth: 1,
        fontSize: 14,
        outline: 'none',
        backgroundColor: (readOnly || disabled) ? '#f1f5f9' : '#fff',
        color: value ? '#1E293B' : '#94A3B8'
      }
    }, [
      // `hidden` keeps the placeholder out of the open list while it still
      // labels the closed box; `disabled` stops it being re-picked.
      React.createElement('option', { value: "", disabled: true, hidden: true, key: "placeholder", style: { color: '#94A3B8' } }, placeholder || 'Select...'),
      ...(options || []).map((opt: any) => React.createElement('option', { value: opt.value, key: opt.value, style: { color: '#1E293B' } }, opt.label))
    ]);
  }

  return (
    <>
      <TouchableOpacity
        style={[styles.input, (readOnly || disabled) && styles.readOnlyInput, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
        onPress={() => !readOnly && !disabled && setIsOpen(true)}
      >
        <Text style={{ color: value ? '#1E293B' : '#94A3B8' }}>{selectedLabel}</Text>
        <FeatherIcon name="chevron-down" size={16} color="#94A3B8" />
      </TouchableOpacity>

      <Modal visible={isOpen} transparent animationType="fade">
        <TouchableWithoutFeedback onPress={() => setIsOpen(false)}>
          <View style={styles.dropdownOverlay}>
            <View style={styles.dropdownMenu}>
              <ScrollView>
                {options?.map((opt: any) => (
                  <TouchableOpacity
                    key={opt.value}
                    style={styles.dropdownOption}
                    onPress={() => { onChange(opt.value); setIsOpen(false); }}
                  >
                    <Text style={[styles.dropdownOptionText, value === opt.value && styles.dropdownOptionSelectedText]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
}


interface Props {
  section: Section;
  formData: Record<string, any>;
  onChange: (name: string, value: any) => void;
  onBlur?: (name: string, value: any) => void;
  errors?: Record<string, string>;
}

export function FormRenderer({ section, formData, onChange, onBlur, errors = {} }: Props) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [collapsed, setCollapsed] = useState(false);

  if (section.condition && !section.condition(formData)) return null;

  // Group dependent fields under their parent
  const topLevelFields = section.fields.filter(f => !f.dependsOn);

  return (
    <View style={[styles.section, isMobile && { marginBottom: 32 }]}>
      {section.title ? (
        <TouchableOpacity
          style={styles.sectionTitleRow}
          onPress={() => setCollapsed(c => !c)}
          activeOpacity={0.8}
        >
          <Text style={styles.sectionTitle}>{section.title}</Text>
          <FeatherIcon name={collapsed ? 'chevron-down' : 'chevron-up'} size={20} color="#FFFFFF" />
        </TouchableOpacity>
      ) : null}

      {!collapsed && (
        <View style={styles.gridContainer}>
          {topLevelFields.map((field, idx) => {
            if (field.condition && !field.condition(formData)) return null;

            const dependentFields = section.fields.filter(
              f => field.name && f.dependsOn === field.name && (!f.showWhen || f.showWhen(formData[field.name]))
            );

            const renderField = (f: Field, isDependent = false, dIdx = 0) => {
              if (f.condition && !f.condition(formData)) return null;
              if (!f.name && f.type !== 'subheading') return null;

              const value = f.name ? formData[f.name] : undefined;
              const readOnly = typeof f.readOnly === 'function' ? f.readOnly(formData) : f.readOnly;

              let content = null;

              if (f.type === 'subheading') {
                return (
                  <View key={`subheading-${idx}-${dIdx}`} style={styles.subheadingContainer}>
                    <Text style={styles.subheadingText}>{f.label}</Text>
                  </View>
                );
              }

              switch (f.type) {
                case 'date':
                  // A real HTML5 date input on web opens the browser's own calendar
                  // picker and guarantees a parseable YYYY-MM-DD value — a plain
                  // TextInput lets users type any text, with no format enforced.
                  // React Native has no built-in equivalent, so native platforms
                  // keep the free-text input with the format spelled out in the label.
                  if (Platform.OS === 'web') {
                    content = React.createElement('input', {
                      type: 'date',
                      value: value || '',
                      disabled: readOnly,
                      onChange: (e: any) => { if (!readOnly) onChange(f.name!, e.target.value); },
                      onBlur: () => { if (!readOnly && onBlur) onBlur(f.name!, value || ''); },
                      style: {
                        boxSizing: 'border-box',
                        width: '100%',
                        height: 42,
                        paddingLeft: 12,
                        paddingRight: 12,
                        borderRadius: 8,
                        border: '1px solid rgb(204, 204, 204)',
                        fontSize: 14,
                        outline: 'none',
                        backgroundColor: readOnly ? '#F1F5F9' : '#FFFFFF',
                        opacity: readOnly ? 0.8 : 1,
                        color: '#1E293B',
                        fontFamily: 'inherit',
                      },
                    });
                    break;
                  }
                // falls through to the shared text-input case on native
                case 'text':
                case 'email':
                case 'tel':
                case 'number':
                  content = (
                    <TextInput
                      style={[styles.input, readOnly && styles.readOnlyInput]}
                      value={value || ''}
                      onChangeText={(val) => {
                        if (!readOnly) {
                          let processedVal = val;
                          if (f.type === 'tel') {
                            processedVal = val.replace(/[^0-9+\-()\s]/g, '');
                          } else if (f.type === 'number') {
                            processedVal = val.replace(/[^0-9]/g, '');
                          }
                          onChange(f.name!, processedVal);
                        }
                      }}
                      onBlur={() => { if (!readOnly && onBlur) onBlur(f.name!, value || '') }}
                      editable={!readOnly}
                      maxLength={f.name && FIELD_LIMITS[f.name] ? FIELD_LIMITS[f.name] : undefined}
                      keyboardType={f.type === 'email' ? 'email-address' : f.type === 'tel' ? 'phone-pad' : 'default'}
                      autoCapitalize={f.type === 'email' ? 'none' : 'sentences'}
                      placeholder={f.placeholder}
                      placeholderTextColor="#94A3B8"
                    />
                  );
                  break;
                case 'textarea':
                  content = (
                    <TextInput
                      style={[styles.input, styles.textarea, readOnly && styles.readOnlyInput]}
                      value={value || ''}
                      onChangeText={(val) => { if (!readOnly) onChange(f.name!, val) }}
                      editable={!readOnly}
                      maxLength={f.name && FIELD_LIMITS[f.name] ? FIELD_LIMITS[f.name] : undefined}
                      multiline
                      numberOfLines={3}
                    />
                  );
                  break;
                case 'radio':
                  content = (
                    <View style={styles.radioGroup}>
                      {f.options?.map((opt) => (
                        <TouchableOpacity
                          key={opt.value}
                          style={styles.radioOption}
                          disabled={readOnly}
                          onPress={() => onChange(f.name!, opt.value)}
                        >
                          <View style={[styles.customBox, value === opt.value && styles.customBoxChecked]}>
                            {value === opt.value && <View style={styles.checkMark} />}
                          </View>
                          <Text style={styles.radioLabel}>{opt.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  );
                  break;
                case 'select':
                  content = (
                    <CustomDropdown
                      options={f.options}
                      value={value}
                      onChange={(val: any) => onChange(f.name!, val)}
                      placeholder={f.placeholder || f.label}
                      readOnly={readOnly}
                    />
                  );
                  break;
                case 'checkbox':
                  content = (
                    <TouchableOpacity
                      style={styles.checkboxOption}
                      disabled={readOnly}
                      onPress={() => onChange(f.name!, !value)}
                    >
                      <View style={[styles.customBox, value && styles.customBoxChecked]}>
                        {value && <View style={styles.checkMark} />}
                      </View>
                      <Text style={styles.radioLabel}>{f.label}</Text>
                    </TouchableOpacity>
                  );
                  break;
                default:
                  content = null;
              }

              // width logic: 32% for columns, 100% for dependencies/subheadings/mobile/single-layout sections/checkboxes
              const fieldWidth = isMobile || isDependent || section.layout === 'single' || f.type === 'checkbox' ? '100%' : '32%';

              return (
                <View key={`${f.name}-${idx}-${dIdx}`} style={[styles.field, { width: fieldWidth as any }, f.type === 'checkbox' && { marginBottom: 8 }]}>
                  {f.type !== 'checkbox' && (
                    <Text style={styles.label}>
                      {f.label} {f.required && <Text style={styles.required}>*</Text>}
                    </Text>
                  )}
                  {content}
                  {f.name && errors[f.name] ? (
                    <Text style={styles.errorText}>{errors[f.name]}</Text>
                  ) : null}
                </View>
              );
            };

            const topFieldEl = renderField(field);

            if (dependentFields.length > 0) {
              return (
                <React.Fragment key={`group-${idx}`}>
                  {topFieldEl}
                  <View style={[styles.dependentWrapper, { width: isMobile ? '100%' : '62%' }]}>
                    {dependentFields.map((depField, dIdx) => renderField(depField, true, dIdx))}
                  </View>
                </React.Fragment>
              );
            }

            return topFieldEl;
          })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: 40, // Increased to ensure clear separation between sections
  },
  sectionTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: THEME_BLUE,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginTop: 8,
    marginBottom: 20,
    borderRadius: 6,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    textTransform: 'uppercase',
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: Platform.OS === 'web' ? '2%' : 0,
    rowGap: 16,
  } as any,
  field: {
    marginBottom: 16,
  },
  subheadingContainer: {
    width: '100%',
    borderBottomWidth: 2,
    borderBottomColor: '#E2E8F0',
    paddingVertical: 8,
    marginTop: 24,
    marginBottom: 12,
  },
  subheadingText: {
    fontWeight: '700',
    textTransform: 'uppercase',
    fontSize: 14,
    color: '#1E293B',
  },
  dependentWrapper: {
    borderLeftWidth: 3,
    borderLeftColor: '#E2E8F0',
    paddingLeft: 12,
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#334155',
    marginBottom: 8,
  },
  required: {
    color: '#EF4444',
    marginLeft: 2,
  },
  input: {
    borderWidth: 1,
    borderColor: '#CCC',
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 42,
    fontSize: 14,
    color: '#1E293B',
    backgroundColor: '#FFFFFF',
  },
  readOnlyInput: {
    backgroundColor: '#F1F5F9',
    opacity: 0.8,
  },
  textarea: {
    height: 80,
    textAlignVertical: 'top',
    paddingVertical: 12,
  },
  radioGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  } as any,
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  checkboxOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 2,
  },
  customBox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#CBD5E1',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  customBoxChecked: {
    backgroundColor: THEME_BLUE,
    borderColor: THEME_BLUE,
  },
  checkMark: {
    position: 'absolute',
    left: 5,
    top: 2,
    width: 4,
    height: 8,
    borderBottomWidth: 2,
    borderRightWidth: 2,
    borderColor: '#FFF',
    transform: [{ rotate: '45deg' }],
  },
  dropdownOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 20,
  },
  dropdownMenu: {
    backgroundColor: '#FFF',
    borderRadius: 8,
    maxHeight: '60%',
    overflow: 'hidden',
  },
  dropdownOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: '#1E293B',
  },
  dropdownOptionSelectedText: {
    color: THEME_BLUE,
    fontWeight: '700',
  },
  radioLabel: {
    fontSize: 15,
    color: '#475569',
    fontWeight: '500',
  },
  errorText: {
    color: '#EF4444',
    fontSize: 12,
    marginTop: 4,
    fontWeight: '500',
  },
});
